/**
 * /coaches — public credibility page.
 *
 * Layout for each coach:
 *   1. Hero — name · title · action photo
 *   2. Identity line — the one-sentence why-us
 *   3. Specialties — quick scan chips
 *   4. Coaching philosophy — the emotional buy-in
 *   5. Credentials — certs, education, athletic background
 *   6. Experience — past roles + outcomes
 *   7. Testimonials (optional)
 *   8. CTA — book Initial session, optionally with this coach
 *
 * Data lives in /lib/coaches.js. Edit there to update content.
 */
import { motion } from "framer-motion";
import {
    Award,
    GraduationCap,
    Dumbbell,
    Briefcase,
    Quote,
    Sparkles,
    Trophy,
    BookOpen,
    Heart,
} from "lucide-react";
import { COACHES } from "@/lib/coaches";

export default function Coaches() {
    return (
        <div data-testid="coaches-page" className="bg-brand-obsidian text-white min-h-screen">
            {/* Page hero */}
            <section className="relative pt-36 pb-16 border-b border-zinc-900">
                <div className="absolute inset-0 bg-grid-faint opacity-30 pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-6 md:px-10">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" /> Our Coaches
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl tracking-wider leading-[0.95] uppercase">
                        Built by athletes.<br />
                        <span className="text-brand-red">Trained like ones.</span>
                    </h1>
                    <p className="mt-7 text-zinc-300 text-base md:text-lg max-w-2xl leading-relaxed">
                        Every program at Maximum Sports Conditioning is owned by a coach who's lived
                        the work — the certifications, the education in the classroom, and the
                        years on the turf and in the weight room. Here's who you'll be standing
                        across from when you walk in.
                    </p>
                </div>
            </section>

            {COACHES.map((coach, idx) => (
                <CoachProfile key={coach.slug} coach={coach} index={idx} />
            ))}
        </div>
    );
}

function CoachProfile({ coach, index }) {
    const isAlt = index % 2 === 1;
    return (
        <section
            data-testid={`coach-${coach.slug}`}
            className={`py-20 ${isAlt ? "bg-zinc-950/30" : ""}`}
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="grid md:grid-cols-12 gap-10">
                    {/* Photo + identity */}
                    <div className="md:col-span-5">
                        <div className="relative">
                            {coach.photo ? (
                                <img
                                    src={coach.photo}
                                    alt={coach.name}
                                    className="w-full aspect-[4/5] object-cover border border-zinc-800"
                                />
                            ) : (
                                <PhotoPlaceholder name={coach.name} />
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2">
                                    {coach.title}
                                </div>
                                <div className="font-display text-3xl tracking-wider uppercase leading-none">
                                    {coach.name}
                                </div>
                            </div>
                        </div>
                        {coach.specialties?.length > 0 && (
                            <div className="mt-5 flex flex-wrap gap-1.5">
                                {coach.specialties.map((s) => (
                                    <span
                                        key={s}
                                        data-testid={`coach-specialty-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className="text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 border border-zinc-700 text-zinc-200 bg-zinc-900/60"
                                    >
                                        {s}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Long-form */}
                    <div className="md:col-span-7 space-y-10">
                        {coach.identity && (
                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4 }}
                                className="text-2xl md:text-3xl font-display tracking-wider leading-snug text-white"
                            >
                                {coach.identity}
                            </motion.p>
                        )}

                        {coach.philosophy?.length > 0 && (
                            <Block icon={Sparkles} title="Coaching Philosophy">
                                {coach.philosophy.map((p, i) => (
                                    <p key={i} className="text-zinc-300 leading-relaxed">{p}</p>
                                ))}
                            </Block>
                        )}

                        {coach.certifications?.length > 0 && (
                            <Block icon={Award} title="Certifications">
                                <ul className="space-y-2">
                                    {coach.certifications.map((c) => (
                                        <li key={c.abbr} className="flex items-start gap-3">
                                            <span className="shrink-0 text-[10px] tracking-[0.2em] uppercase px-2 py-1 bg-brand-red text-white font-semibold">
                                                {c.abbr}
                                            </span>
                                            <span className="text-zinc-300 text-sm leading-relaxed">{c.full}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Block>
                        )}

                        {coach.education?.length > 0 && (
                            <Block icon={GraduationCap} title="Education">
                                <ul className="space-y-3">
                                    {coach.education.map((e, i) => (
                                        <li key={i} className="text-zinc-300">
                                            <div className="text-white">{e.degree}</div>
                                            <div className="text-sm text-zinc-400">
                                                {e.institution}
                                                {e.years && <span> · {e.years}</span>}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Block>
                        )}

                        {coach.athleticBackground?.length > 0 && (
                            <Block icon={Dumbbell} title="Coaching Background">
                                <ul className="space-y-1.5 text-zinc-300">
                                    {coach.athleticBackground.map((b, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className="text-brand-red mt-2 w-1 h-1 rounded-full bg-brand-red shrink-0" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </Block>
                        )}

                        {coach.experience?.length > 0 && (
                            <Block icon={Briefcase} title="Experience">
                                <ul className="space-y-3">
                                    {coach.experience.map((e, i) => (
                                        <li key={i} className="border-l-2 border-brand-red/40 pl-4">
                                            <div className="text-white">{e.role}</div>
                                            <div className="text-sm text-zinc-400">
                                                {e.org}{e.years && <span> · {e.years}</span>}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Block>
                        )}

                        {coach.athletesCoached?.length > 0 && (
                            <Block icon={Trophy} title="Athletes Coached">
                                <ul className="space-y-1.5 text-zinc-300">
                                    {coach.athletesCoached.map((a, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className="text-brand-red mt-2 w-1 h-1 rounded-full bg-brand-red shrink-0" />
                                            {a}
                                        </li>
                                    ))}
                                </ul>
                            </Block>
                        )}

                        {coach.continuingEducation?.length > 0 && (
                            <Block icon={BookOpen} title="Currently studying">
                                <ul className="space-y-1.5 text-zinc-300 text-sm">
                                    {coach.continuingEducation.map((c, i) => (
                                        <li key={i}>{c}</li>
                                    ))}
                                </ul>
                            </Block>
                        )}

                        {coach.personal?.length > 0 && (
                            <Block icon={Heart} title="Outside the gym">
                                <ul className="space-y-1.5 text-zinc-300 text-sm">
                                    {coach.personal.map((p, i) => (
                                        <li key={i} data-testid={`coach-${coach.slug}-personal-${i}`} className="flex items-start gap-2">
                                            <span className="text-brand-red mt-2 w-1 h-1 rounded-full bg-brand-red shrink-0" />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </Block>
                        )}

                        {coach.testimonials?.length > 0 && (
                            <Block icon={Quote} title="In their words">
                                <div className="space-y-5">
                                    {coach.testimonials.map((t, i) => (
                                        <blockquote
                                            key={i}
                                            className="border-l-2 border-brand-red/60 pl-5"
                                        >
                                            <p className="text-zinc-200 leading-relaxed italic">
                                                "{t.text}"
                                            </p>
                                            <footer className="mt-2 text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                                                — {t.name}{t.role && ` · ${t.role}`}
                                            </footer>
                                        </blockquote>
                                    ))}
                                </div>
                            </Block>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Block({ icon: Icon, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <Icon className="w-4 h-4 text-brand-red" />
                <h3 className="font-display text-xl tracking-wider uppercase text-white">{title}</h3>
                <div className="flex-1 h-px bg-zinc-900 ml-2" />
            </div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function PhotoPlaceholder({ name }) {
    const initials = name.split(" ").map((n) => n[0]).slice(0, 2).join("");
    return (
        <div className="w-full aspect-[4/5] bg-gradient-to-br from-brand-navy-900 via-zinc-900 to-black border border-zinc-800 flex items-center justify-center">
            <div className="text-center px-6">
                <div className="font-display text-6xl tracking-wider text-zinc-700">{initials}</div>
                <div className="mt-3 text-[10px] tracking-[0.2em] uppercase text-zinc-600">
                    Photo coming soon
                </div>
            </div>
        </div>
    );
}
